import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, FileText, Download, CheckCircle, AlertCircle, 
  RefreshCw, Clock, User, FolderOpen, Play, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const colors = {
    INCOMPLETE: 'bg-yellow-100 text-yellow-800',
    READY_FOR_OPINION: 'bg-green-100 text-green-800',
    OPENED_BY_ADVOCATE: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-gray-100 text-gray-800'
  };
  
  const labels = {
    INCOMPLETE: 'Incomplete',
    READY_FOR_OPINION: 'Ready for Opinion',
    OPENED_BY_ADVOCATE: 'In Progress',
    COMPLETED: 'Completed'
  };
  
  return (
    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${colors[status]}`}>
      {labels[status] || status}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const colors = {
    MANDATORY: 'bg-red-100 text-red-700',
    CONDITIONAL: 'bg-orange-100 text-orange-700',
    OPTIONAL: 'bg-green-100 text-green-700'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[priority]}`}>
      {priority}
    </span>
  );
};

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchCaseData();
  }, [caseId]);

  const fetchCaseData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/missing-docs/case/${caseId}`);
      setCaseData(data);
    } catch (err) {
      console.error('Failed to fetch case:', err);
      setError(err.response?.data?.detail || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadZip = async () => {
    setActionLoading('zip');
    try {
      const response = await axios.get(
        `${API_URL}/api/missing-docs/case/${caseId}/download-zip`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${caseId}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadChecklist = async () => {
    setActionLoading('checklist');
    try {
      const response = await axios.get(
        `${API_URL}/api/missing-docs/download-checklist/${caseId}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${caseId}_checklist.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download checklist: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkComplete = async () => {
    if (!window.confirm('Mark this case as completed?')) return;
    
    setActionLoading('complete');
    try {
      await axios.post(`${API_URL}/api/missing-docs/case/${caseId}/complete`);
      await fetchCaseData();
      alert('Case marked as completed!');
    } catch (err) {
      alert('Failed to complete case: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartOpinion = () => {
    // Navigate to the extraction page to start opinion generation
    // Pass case info via state or query params
    navigate('/extract', { 
      state: { 
        fromCase: caseId,
        caseName: caseData?.case_name,
        propertyType: caseData?.property_type,
        profile: caseData?.profile
      } 
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading case...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900">Error Loading Case</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!caseData) return null;

  const uploadedDocs = caseData.uploaded_docs || [];
  const missingDocs = caseData.missing_docs || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="case-detail-page">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-500 hover:text-gray-700 flex items-center"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                Back
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {caseData.case_name || caseData.case_id}
                </h1>
                <p className="text-sm text-gray-500">Case ID: {caseData.case_id}</p>
              </div>
            </div>
            <StatusBadge status={caseData.status} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Case Info Card */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Case Information</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Profile</p>
              <p className="font-semibold text-gray-900">{caseData.profile}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Property Type</p>
              <p className="font-semibold text-gray-900">{caseData.property_type?.replace('_', ' ')}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Completeness</p>
              <p className="font-semibold text-green-600">{caseData.completeness_percent}%</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Documents</p>
              <p className="font-semibold text-gray-900">{uploadedDocs.length} uploaded</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-gray-400" />
              <span>Created: {formatDate(caseData.created_at)}</span>
            </div>
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2 text-gray-400" />
              <span>Created by: {caseData.created_by || 'Unknown'}</span>
            </div>
            {caseData.marked_ready_at && (
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                <span>Marked ready: {formatDate(caseData.marked_ready_at)}</span>
              </div>
            )}
            {caseData.advocate_id && (
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2 text-blue-500" />
                <span>Advocate: {caseData.advocate_id}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions Card */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-100 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-blue-900">Actions</h2>
          
          <div className="flex flex-wrap gap-3">
            {/* Start Opinion Generation - Primary Action */}
            {caseData.status !== 'COMPLETED' && (
              <button
                onClick={handleStartOpinion}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center font-medium shadow-sm"
                data-testid="start-opinion-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Opinion Generation
              </button>
            )}
            
            {/* Download ZIP */}
            <button
              onClick={handleDownloadZip}
              disabled={actionLoading === 'zip'}
              className="px-4 py-3 bg-white text-gray-700 rounded-lg hover:bg-gray-50 flex items-center border shadow-sm disabled:opacity-50"
              data-testid="download-zip-btn"
            >
              {actionLoading === 'zip' ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download All Documents (ZIP)
            </button>
            
            {/* Download Checklist */}
            <button
              onClick={handleDownloadChecklist}
              disabled={actionLoading === 'checklist'}
              className="px-4 py-3 bg-white text-gray-700 rounded-lg hover:bg-gray-50 flex items-center border shadow-sm disabled:opacity-50"
              data-testid="download-checklist-btn"
            >
              {actionLoading === 'checklist' ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Download Checklist
            </button>
            
            {/* Mark Complete */}
            {caseData.status === 'OPENED_BY_ADVOCATE' && (
              <button
                onClick={handleMarkComplete}
                disabled={actionLoading === 'complete'}
                className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center shadow-sm disabled:opacity-50"
                data-testid="mark-complete-btn"
              >
                {actionLoading === 'complete' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Mark as Completed
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Uploaded Documents */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4 text-green-700 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Uploaded Documents ({uploadedDocs.length})
            </h2>
            
            {uploadedDocs.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No documents uploaded</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {uploadedDocs.map((doc, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{doc.file_name}</p>
                        <p className="text-sm text-gray-500">{doc.doc_type_label || doc.doc_type}</p>
                      </div>
                    </div>
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Missing Documents (if any) */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-orange-500" />
              Document Checklist
            </h2>
            
            {missingDocs.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className="text-green-700 font-medium">All required documents are present!</p>
                <p className="text-sm text-gray-500">This case is ready for opinion generation.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {missingDocs.map((doc, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">{doc.doc_type_label || doc.doc_type}</span>
                      <PriorityBadge priority={doc.priority} />
                    </div>
                    <p className="text-xs text-gray-600">{doc.reason}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notes Section */}
        {caseData.notes && caseData.notes.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4">Notes</h2>
            <div className="space-y-2">
              {caseData.notes.map((note, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">{note.text}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatDate(note.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
